import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Overlay from "../../../components/Overlay";
import Button from "../../../components/common/Button";
import useUsers from "../../../hooks/useUsers";
import { format } from "date-fns";
import * as Tooltip from "react-tooltip";
import useTendering from "../../../hooks/useTendering";
import _ from "lodash";
import { toast } from "react-toastify";

const assetsUri = import.meta.env.ASSETS_URI;

const Participants = ({ tenderingId, onClose, participants, onChange }) => {
  const [init, setInit] = useState(false);
  const [datasource, setDatasource] = useState([]);
  const { queryUsers } = useUsers();
  const { sendNDA, rejectParticipant, update } = useTendering();
  const [acceptedParticipants, setAcceptedParticipants] = useState([]);
  const [canContinue, setCanContinue] = useState(false);

  useEffect(() => {
    if (init) return;
    if (participants.length === 0) return;

    const _userIds = participants.map((p) => p.user);

    queryUsers(
      {
        _id: {
          $in: _userIds,
        },
      },
      undefined,
      -1,
      {
        avatar: 1,
        firstname: 1,
        surname: 1,
      }
    ).then(({ data: users }) => {
      // * Map participants and users to set datasource
      const _datasource = participants.map((p) => {
        const _user = users.find((u) => u._id === p.user);

        return {
          ...p,
          user: {
            _id: p.user,
            avatar: `${assetsUri}/${_user.avatar}`,
            name: `${_user.firstname} ${_user.surname ?? ""}`,
          },
        };
      });

      setDatasource(_datasource);
      setInit(true);
    });
  }, [participants, init]);

  useEffect(() => {
    let _acceptedParticipants = datasource.filter(
      (data) => data.accepted && !data.rejected
    );

    setAcceptedParticipants(_acceptedParticipants);
    setCanContinue(
      _acceptedParticipants.length > 0 && _acceptedParticipants.length <= 3
    );

    // * Trigger on change
    if (!onChange) return;

    onChange({
      participants: datasource.map((data) => ({
        ...data,
        user: data.user._id,
      })),
    });
  }, [datasource]);

  return (
    <Overlay onClose={onClose}>
      <div className="tendering-detail__participants">
        <section className="tendering-detail__participants__header">
          <span className="tendering-detail__participants__header__title">
            Participants
          </span>
          <div className="tendering-detail__participants__header__selections">
            {acceptedParticipants.length}
            /3
          </div>
        </section>
        <div className="tendering-detail__participants__content__scroller">
          <div className="tendering-detail__participants__content">
            {(!participants || participants.length === 0) && (
              <span className="tendering-detail__participants__content__empty">
                At this time, there are no participants involved in your project
              </span>
            )}
            {datasource?.map(
              ({ user, nda, rejected, accepted, createdOn }, index) => {
                let _status = undefined;

                if (nda.sent && !nda.signed) {
                  _status = {
                    label: "Waiting",
                    color: "#A66A01",
                    background: "#FFDB4F",
                    tooltip: "Waiting signing of NDA",
                  };
                }

                if (accepted) {
                  _status = {
                    label: "Accepted",
                    color: "#fff",
                    background: "#14D886",
                    tooltip: `NDA signed on ${format(
                      new Date(nda.signedOn),
                      "dd MMM yyyy"
                    )}`,
                  };
                }

                if (rejected) {
                  _status = {
                    label: "Rejected",
                    color: "#fff",
                    background: "#DC0505",
                  };
                }

                return (
                  <div
                    className="tendering-detail__participants__content__participant"
                    key={user._id}
                  >
                    <img
                      className="tendering-detail__participants__content__participant__avatar"
                      src={user.avatar}
                    />
                    <span className="tendering-detail__participants__content__participant__name">
                      {user.name}
                    </span>
                    <span className="tendering-detail__participants__content__participant__date">
                      {format(new Date(createdOn), "dd MMM yyyy")}
                    </span>
                    {_status && (
                      <span
                        className="tendering-detail__participants__content__participant__status"
                        data-tooltip-id="status-hint"
                        data-tooltip-content={_status.tooltip}
                        style={{
                          color: _status.color,
                          background: _status.background,
                        }}
                      >
                        {_status.label}
                      </span>
                    )}
                    <div className="tendering-detail__participants__content__participant__reject">
                      <Button
                        label={"Reject"}
                        variant={"secondary"}
                        size="small"
                        disabled={rejected}
                        onClick={async () => {
                          rejectParticipant(tenderingId, user._id).then(
                            (result) => {
                              if (!result) {
                                // TODO: Handle error
                                return;
                              }

                              setDatasource((curr) => {
                                let _curr = _.cloneDeep(curr);
                                _curr[index].rejected = true;
                                _curr[index].rejectedOn = new Date();
                                return _curr;
                              });

                              toast.info("Rejected participant");
                            }
                          );
                        }}
                      />
                    </div>
                    <div className="tendering-detail__participants__content__participant__accept">
                      <Button
                        label={"Send NDA"}
                        size="small"
                        disabled={nda.sent}
                        onClick={() => {
                          sendNDA(tenderingId, user._id).then((result) => {
                            if (!result) {
                              // TODO: Handle error
                              return;
                            }

                            setDatasource((curr) => {
                              let _curr = _.cloneDeep(curr);
                              _curr[index].nda.sent = true;
                              _curr[index].nda.sentOn = new Date();
                              return _curr;
                            });

                            toast.success("NDA sent");
                          });
                        }}
                      />
                    </div>
                  </div>
                );
              }
            )}
          </div>
        </div>
        <div
          className="tendering-detail__participants__footer"
          data-tooltip-id="status-hint"
          data-tooltip-content={
            !canContinue ? "Too many or no participants have been selected" : ""
          }
        >
          <Button
            label="Confirm participants and continue"
            width={"100%"}
            disabled={!canContinue}
            onClick={() => {
              update(tenderingId, { status: 2 }).then((result) => {
                if (result.status !== 2) {
                  alert("error");
                  return;
                }

                onChange({
                  status: 2,
                });
              });
            }}
          />
        </div>
      </div>
      <Tooltip.Tooltip id="status-hint" />
    </Overlay>
  );
};

Participants.propTypes = {};

export default Participants;
